import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
} from '@mui/material';
import { Refresh, Assessment } from '@mui/icons-material';
import apiClient from '../../api';

function formatWatchTime(seconds) {
    const s = Number(seconds) || 0;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, '0')}`;
}

function statusColor(status) {
    switch (status) {
        case 'completed':
            return 'success';
        case 'in_progress':
            return 'warning';
        case 'overdue':
            return 'error';
        default:
            return 'default';
    }
}

/**
 * Company-admin / video-admin table of per-user watch progress.
 */
export default function SafetyTrainingCompliance() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [videos, setVideos] = useState([]);
    const [rows, setRows] = useState([]);
    const [videoFilter, setVideoFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const fetchReport = async () => {
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (videoFilter !== 'all') params.contentId = videoFilter;
            if (statusFilter !== 'all') params.status = statusFilter;
            const res = await apiClient.get('/safety/tracking/report', { params });
            setVideos(res.data?.data?.videos || []);
            setRows(res.data?.data?.rows || []);
        } catch (err) {
            console.error('Watch report error:', err);
            setError(err.response?.data?.message || 'Failed to load watch report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoFilter, statusFilter]);

    const summary = useMemo(() => {
        const total = rows.length;
        const completed = rows.filter((r) => r.status === 'completed').length;
        const inProgress = rows.filter((r) => r.status === 'in_progress').length;
        return { total, completed, inProgress };
    }, [rows]);

    if (loading && rows.length === 0) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={2}
                mb={3}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Assessment color="primary" />
                    <Typography variant="h5">Training watch tracking</Typography>
                </Stack>
                <Button startIcon={<Refresh />} variant="outlined" onClick={fetchReport} disabled={loading}>
                    Refresh
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="video-filter">Video</InputLabel>
                    <Select
                        labelId="video-filter"
                        label="Video"
                        value={videoFilter}
                        onChange={(e) => setVideoFilter(e.target.value)}
                    >
                        <MenuItem value="all">All videos</MenuItem>
                        {videos.map((v) => (
                            <MenuItem key={v._id} value={v._id}>
                                {v.title}
                                {!v.isPublished ? ' (Draft)' : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="status-filter">Status</InputLabel>
                    <Select
                        labelId="status-filter"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <MenuItem value="all">All statuses</MenuItem>
                        <MenuItem value="not_started">Not started</MenuItem>
                        <MenuItem value="in_progress">In progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="overdue">Overdue</MenuItem>
                    </Select>
                </FormControl>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={`${summary.completed} completed`} color="success" size="small" />
                    <Chip label={`${summary.inProgress} in progress`} color="warning" size="small" />
                    <Chip label={`${summary.total} rows`} size="small" />
                </Stack>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Video</TableCell>
                            <TableCell>Viewer</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Progress</TableCell>
                            <TableCell>Last viewed</TableCell>
                            <TableCell align="right">Watch time</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        No tracking rows yet. Upload Ready videos and have company users watch them.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={`${row.videoId}-${row.userId}`}>
                                    <TableCell>
                                        <Stack spacing={0.5}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {row.videoTitle}
                                            </Typography>
                                            {!row.videoReady && (
                                                <Chip label="Draft" size="small" sx={{ width: 'fit-content' }} />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{row.userName}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {row.userEmail}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={String(row.status || 'not_started').replace('_', ' ')}
                                            color={statusColor(row.status)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{row.progressPercentage ?? 0}%</TableCell>
                                    <TableCell>
                                        {row.lastViewedAt
                                            ? new Date(row.lastViewedAt).toLocaleString()
                                            : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatWatchTime(row.watchTimeSeconds)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
